/* eslint-disable no-unused-vars */
/* eslint-disable react/jsx-props-no-spreading */
import React, { Fragment, useState } from "react";
import { object, arrayOf, PropTypes } from "prop-types";
import Downshift from "downshift";
import cx from "classnames";
import { navigate } from "gatsby";

import { Icon } from "../Icons";
import { truncateString } from "../FAQSearch/utils/truncateString";
import { getFaqSubCatURL } from "../FAQSearch/utils/getFaqSubCatURL";
import { RCL as useTranslation } from "../RCL";
import * as styles from "./FaqsTypeahead.module.scss";

const FaqsTypeahead = ({ data, emptyMessage, viewAllLink, submitHandler, locale, className }) => {
	const [isDisabled, setIsDisabled] = useState(true);
	const topResultFoundLabel = useTranslation({
		searchKey: "top-search-results",
	});
	const searchPlaceholder = useTranslation({
		searchKey: "find-what-you-are-looking-for",
	});

	return (
		<Downshift
			itemToString={item => (item ? item.question : "")}
			onSelect={selectedItem => {
				if (!selectedItem.ctf_id) {
					return navigate(`/${locale}/faqs/search/?q=${selectedItem?.question}`);
				}
				return navigate(
					`${getFaqSubCatURL(locale, selectedItem?.subCategory)}/#${selectedItem?.slug}`
				);
			}}
		>
			{({
				getInputProps,
				getItemProps,
				getMenuProps,
				isOpen,
				inputValue,
				getRootProps,
				closeMenu,
				highlightedIndex,
				getToggleButtonProps,
			}) => {
				let filterResult = null;
				setIsDisabled(true);

				if (inputValue?.length > 2) {
					setIsDisabled(false);

					filterResult = data
						.filter(
							item =>
								item?.contentString?.toLowerCase()?.includes(inputValue?.toLowerCase()) ||
								item?.question?.toLowerCase()?.includes(inputValue?.toLowerCase())
						)
						.sort((a, b) => b.order - a.order);
					if (filterResult.length === 0) {
						setIsDisabled(true);
					}
				}

				const submitQuery = () => {
					if (filterResult?.length === 0) {
						return;
					}
					if (inputValue.length > 2) {
						closeMenu();
						submitHandler(inputValue);
					}
				};

				return (
					<>
						<div
							className={cx(
								styles.inputBox,
								className,
								inputValue.length > 2 && styles?.expandInput
							)}
							{...getRootProps({}, { suppressRefError: true })}
						>
							<input
								{...getInputProps()}
								className={cx(styles.FormControl, isOpen && styles.open)}
								// eslint-disable-next-line no-unneeded-ternary
								placeholder={searchPlaceholder}
							/>
							<button
								{...getToggleButtonProps()}
								aria-label="button"
								onClick={submitQuery}
								type="button"
								className={styles.iconButton}
							>
								<Icon name="search" className={cx(styles.iconSearch, isDisabled && styles?.grey)} />
							</button>

							{isOpen && filterResult?.length > 0 ? (
								<ul {...getMenuProps()} className={cx(styles.listBox, isOpen && styles.open)}>
									<li className={styles?.topSearchResult}>
										<strong className={styles.topResults}>{topResultFoundLabel}</strong>
									</li>
									{filterResult?.slice(0, 4).map((item, index) => (
										// eslint-disable-next-line react/jsx-indent
										<li
											className={highlightedIndex === index && styles?.blue}
											{...getItemProps({
												key: item.id,
												index,
												item,
											})}
										>
											<div className={styles.listLink}>
												<span className={styles?.listQuestion}>
													{" "}
													{truncateString(item.question, inputValue, 60)}
												</span>
												<p className={styles.listAnswer}>
													{truncateString(item?.contentString, inputValue, 60)}
												</p>
											</div>
										</li>
									))}
									<li
										className={highlightedIndex === filterResult?.length && styles?.blue}
										{...getItemProps({
											key: "viewAll",
											index: filterResult?.length,
											item: { question: inputValue },
										})}
									>
										<button onClick={submitQuery} type="button" className={styles.viewAllButton}>
											{viewAllLink}
										</button>
									</li>
								</ul>
							) : (
								isOpen &&
								filterResult?.length === 0 && (
									<ul {...getMenuProps()} className={cx(styles.listBox, isOpen && styles.open)}>
										<li>
											<b>{emptyMessage}</b>
										</li>
									</ul>
								)
							)}
						</div>
					</>
				);
			}}
		</Downshift>
	);
};

FaqsTypeahead.propTypes = {
	data: arrayOf(object).isRequired,
	emptyMessage: PropTypes.string,
	viewAllLink: PropTypes.string,
	submitHandler: PropTypes.func,
	locale: PropTypes.string,
	className: PropTypes.string,
};

FaqsTypeahead.defaultProps = {
	emptyMessage: "No Results Found",
	viewAllLink: "View all >>",
	submitHandler: () => {},
	locale: "en",
	className: null,
};

export default FaqsTypeahead;
export { FaqsTypeahead };
